import React, {
  memo, useEffect, useMemo, useState,
} from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import axios from 'axios'
import { FormattedMessage, useIntl } from 'react-intl'
import Grid from '@mui/material/Grid2'
import Page from 'components/layout/Page'
import UploadIcon from 'components/icons/icon-park-solid_doc-search.svg'
import usePageProps from 'hooks/usePageProps'
import { Box, Button, Typography } from '@mui/material'
import { useModal } from 'components/shared/Modal'
import { useRouter } from 'next/router'
import EventEmitter from 'services/EventEmitter'
import { useDigitalExpedientMutation, useDocumentMutation, useDigitalExpedient } from 'hooks/useDigitalExpedient'
import isEmpty from 'lodash/isEmpty'
import { useQueryClient } from 'react-query'
import useStatusSections from './Steps/Overview/useStatusSections'
import sx from './styles'

const LoadingDialogDynamic = dynamic(() => import('components/UI/LoadingDialog'))
const OverviewDynamic = dynamic(() => import('./Steps/Overview'))
const OficialIdDynamic = dynamic(() => import('./Steps/OficialIdFront'))
const OficialIdBackDynamic = dynamic(() => import('./Steps/OficialIdBack'))
const OficialIdOldDynamic = dynamic(() => import('./Steps/OficialIdOldFront'))
const OficialIdOldBackDynamic = dynamic(() => import('./Steps/OficialIdOldBack'))
const CirculationCardFrontDynamic = dynamic(() => import('./Steps/CirculationCardFront'))
const CirculationCardBackDynamic = dynamic(() => import('./Steps/CirculationCardBack'))
const InvoiceFrontDynamic = dynamic(() => import('./Steps/InvoiceFront'))
const InvoiceBackDynamic = dynamic(() => import('./Steps/InvoiceBack'))
const InvoiceOldFrontDynamic = dynamic(() => import('./Steps/InvoiceOldFront'))
const InvoiceOldBackDynamic = dynamic(() => import('./Steps/InvoiceOldBack'))
const InvoiceOld2FrontDynamic = dynamic(() => import('./Steps/InvoiceOld2Front'))
const InvoiceOld2BackDynamic = dynamic(() => import('./Steps/InvoiceOld2Back'))
const ErrorModalDynamic = dynamic(() => import('components/shared/ErrorModal'))

const getDigitaExpedientPath = async (digest) => {
  const url = `${process.env.NEXT_PUBLIC_API_ROOT}/api/expedients/digest/${digest}`
  return (axios.get(url))
}

function DigitalExpedient() {
  const router = useRouter()
  const { formatMessage: f } = useIntl()
  const { params } = usePageProps()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [expId, setExpId] = useState(null)
  const [tokenAuth, setToken] = useState(null)
  const digExpedientMutation = useDigitalExpedientMutation()
  const documentsMutation = useDocumentMutation()
  const { handleOpenModal, handleCloseModal } = useModal()
  const isEasyTypeRoot = useMemo(() => router.asPath.includes('expediente-digital-easy'), [router.asPath])
  const nextSection = `${f({ id: isEasyTypeRoot ? 'ROUTE_DIG_EXPEDIENT_EASY' : 'ROUTE_DIG_EXPEDIENT' }, { step: f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_OVERVIEW' }) })}`

  const digExpQuery = useDigitalExpedient(
    expId, tokenAuth, Boolean(expId && tokenAuth),
  )
  const { isFinished } = useStatusSections(digExpQuery?.data, isEasyTypeRoot)
  const overviewStep = f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_OVERVIEW' })
  const overviewRoute = f({
    id: isEasyTypeRoot
      ? 'ROUTE_DIG_EXPEDIENT_EASY'
      : 'ROUTE_DIG_EXPEDIENT',
  }, { step: overviewStep })

  const handleSubmit = (formData = {}, callback) => {
    setLoading(true)
    const expedientData = formData?.expedient
    const documentsData = formData?.documents

    if (!isEmpty(documentsData)) {
      documentsMutation.mutate(
        {
          expId,
          tokenAuth,
          data: documentsData,
        },
        {
          onSuccess: (documentData) => {
            const documents = documentData?.documents
            const expToSend = {
              ...digExpQuery?.data,
              documents,
            }
            if (isEmpty(expedientData)) {
              setLoading(false)
              queryClient.setQueryData(
                ['digital-expedient', { expId, tokenAuth }],
                expToSend,
              )
              if (callback && typeof callback === 'function') {
                callback(documentData)
              }
            } else {
              digExpedientMutation.mutate(
                {
                  expId,
                  tokenAuth,
                  data: {
                    questions: {
                      ...digExpQuery?.data?.questions,
                      ...expedientData,
                    },
                  },
                },
                {
                  onSuccess: (expData) => {
                    setLoading(false)
                    const questions = { ...expData?.questions, ...expToSend?.questions }

                    queryClient.setQueryData(
                      ['digital-expedient', { expId, tokenAuth }],
                      {
                        ...expToSend,
                        questions,
                      },
                    )
                    if (callback && typeof callback === 'function') {
                      callback(documentData)
                    }
                  },
                },
                {
                  onError: (err) => {
                    // eslint-disable-next-line no-console
                    console.log('error', err)
                    setLoading(false)
                  },
                },
              )
            }
          },
        },
        {
          onError: (err) => {
            // eslint-disable-next-line no-console
            console.error('error', err)
            setLoading(false)
          },
        },
      )
    }
  }

  const handleCloseErrorModal = () => {
    handleCloseModal()
    router.push('/404')
  }

  const handleErrorModal = () => {
    handleOpenModal({
      title: 'ERROR_MODAL_TITLE',
      body: (
        <ErrorModalDynamic
          onCloseModal={handleCloseErrorModal}
        />
      ),
      configProps: {
        sxProp: { title: sx.modalTitle },
        isFormattedTitle: true,
      },
    })
  }

  const STEPS = {
    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_OVERVIEW' })]: (
      <OverviewDynamic
        isFinished={isFinished}
        expedient={digExpQuery?.data}
        expId={expId}
        isEasyType={isEasyTypeRoot}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_ID_FRONT_EASY' })]: (
      <OficialIdOldDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_ID_FRONT' })]: (
      <OficialIdDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_ID_BACK_EASY' })]: (
      <OficialIdOldBackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_ID_BACK' })]: (
      <OficialIdBackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_CIRCULATION_CARD_FRONT' })]: (
      <CirculationCardFrontDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_CIRCULATION_CARD_BACK' })]: (
      <CirculationCardBackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_FRONT' })]: (
      <InvoiceFrontDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={isFinished}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_FRONT_EASY' })]: (
      <InvoiceOldFrontDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={false}
        expedient={digExpQuery?.data}
        isEasyType={isEasyTypeRoot}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_FRONT_EASY_2' })]: (
      <InvoiceOld2FrontDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        isFinished={false}
        expedient={digExpQuery?.data}
        isEasyType={isEasyTypeRoot}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_BACK' })]: (
      <InvoiceBackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        expedient={digExpQuery?.data}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_BACK_EASY' })]: (
      <InvoiceOldBackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        expedient={digExpQuery?.data}
        isEasyType={isEasyTypeRoot}
      />
    ),

    [f({ id: 'ROUTE_DIG_EXPEDIENT_STEP_INVOICE_BACK_EASY_2' })]: (
      <InvoiceOld2BackDynamic
        onSubmit={handleSubmit}
        setLoading={setLoading}
        expedient={digExpQuery?.data}
        isEasyType={isEasyTypeRoot}
      />
    ),
  }

  const dataRequestDialog = {
    title: 'Espera unos segundos...',
    icon: <UploadIcon />,
    description: 'Estamos cargando la foto.',
  }

  const onCloseModal = () => {
    handleCloseModal()
    router.push(nextSection)
  }

  const onCancelDigitalExpedient = () => {
    handleOpenModal({
      title: f({ id: 'DIG_EXPEDIENT_ARE_YOU_SHURE_TO_EXIT' }),
      body: (
        <>
          <Box>
            <Typography>
              <FormattedMessage id="DIG_EXPEDIENT_MODAL_DESCRIPTION" />
            </Typography>
            <Box mt={2}>
              <Button
                variant="special"
                color="primary"
                fullWidth
                onClick={onCloseModal}
              >
                Aceptar
              </Button>
            </Box>
          </Box>
        </>
      ),
      configProps: {
        showDividers: false,
        showClose: true,
        maxWidth: 'xs',
      },
    })
  }

  useEffect(() => {
    if (router?.query?.uuid) {
      setExpId(router.query.uuid)
      localStorage.setItem('uuid', router?.query?.uuid)
    } else {
      const storageExpId = localStorage.getItem('uuid') || null
      setExpId(storageExpId)
    }
    if (router?.query?.token) {
      setToken(router.query.token)
      localStorage.setItem('token', router?.query?.token)
    } else {
      const storageToken = localStorage.getItem('token') || null
      setToken(storageToken)
    }
  }, [router?.query])

  useEffect(() => {
    if (digExpQuery?.isError) {
      handleErrorModal()
    }
  }, [digExpQuery?.isError])

  useEffect(() => {
    EventEmitter.suscribe('back-to-dig-exp', onCancelDigitalExpedient)
    return () => {
      EventEmitter.unsubscribe('back-to-dig-exp')
      localStorage.removeItem('uuid')
      localStorage.removeItem('token')
    }
  }, [])

  useEffect(() => {
    if (router?.asPath?.includes(overviewRoute)) {
      if (router?.query?.digest) {
        getDigitaExpedientPath(router?.query?.digest)
          .then(({ data }) => {
            const queryData = {
              uuid: data?.uuid,
              token: data?.auth,
            }
            router.push({
              pathname: overviewRoute,
              query: queryData,
            })
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            router.push('/404')
            // eslint-disable-next-line no-console
            console.error('errr', error)
          })
      }
    }
  }, [isEasyTypeRoot, router?.query])

  return (
    <>
      <Head>
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      </Head>
      <Page variant="bg-grey">
        <Grid
          container
          justifyContent="center"
        >
          <Grid
            container
            justifyContent="center"
            sx={sx.root}
          >
            <Grid
              size={12}
              sx={sx.stepContainer}
            >
              {STEPS[params?.step]}
            </Grid>
          </Grid>
        </Grid>
      </Page>

      <LoadingDialogDynamic
        open={loading}
        {...dataRequestDialog}
      />
    </>
  )
}

export default memo(DigitalExpedient)
